import { useCallback, useMemo } from 'react';

import { analytics } from '../../services/analytics';
import { useFlag } from '../../services/launchDarkly';
import type { FullPageDialogs } from '../../stores/ui/types';
import { useStores } from '../../stores/useStores';
import { ACCOUNT_RELATED_DIALOGS } from '../FullPageDialogs/config';

export const useFooterNavigationLogic = () => {
  const { uiStore, loyaltyProgramStore, bingoStore } = useStores();
  const redeemModeAvailable = useFlag('redeem_mode_available', true);

  const preventPurchase = useFlag('prevent_purchase', false);

  const handleOpenFullPageModal = useCallback(
    (dialog: FullPageDialogs | 'home' | null, disabled = false) => {
      if (disabled) {
        return;
      }
      if (dialog === 'store' && preventPurchase) {
        uiStore.setOpenFullPageDialog('prevent_purchase');
        return;
      }
      uiStore.setOpenFullPageDialog(dialog === 'home' ? null : dialog);
      void analytics.sendEvent('lobby_navigation', {
        event_type: 'lobby_type',
        event_action: 'click',
        event_category: 'account',
        event_value: dialog || '',
      });
    },
    [preventPurchase, uiStore],
  );

  const handleRewardOpen = useCallback(() => {
    handleOpenFullPageModal('rewards');
    void analytics.sendEvent('lobby_navigation', {
      event_type: 'lobby_type',
      event_action: 'click',
      event_category: 'account',
      event_value: 'lounge',
    });
  }, [handleOpenFullPageModal]);

  const handleOpenStore = useCallback(
    (disabled = false) => {
      if (disabled) {
        return;
      }
      void analytics.sendEvent('lobby_navigation', {
        event_type: 'lobby_type',
        event_action: 'click',
        event_category: 'account',
        event_value: 'store',
      });

      handleOpenFullPageModal('store');
    },
    [handleOpenFullPageModal],
  );

  const handleOpenRedeem = useCallback(
    (disabled = false) => {
      if (disabled || uiStore.openFullPageDialog === 'redeem') {
        return;
      }
      void analytics.sendEvent('lobby_navigation', {
        event_type: 'lobby_type',
        event_action: 'click',
        event_category: 'account',
        event_value: 'redeem',
      });

      handleOpenFullPageModal('redeem');
    },
    [handleOpenFullPageModal, uiStore.openFullPageDialog],
  );

  const handleOpenBingo = useCallback(
    (disabled = false) => {
      if (disabled) {
        return;
      }
      void analytics.sendEvent('bingo_button_clicked', {
        triggered_by: 'footer_nav',
        event_action: 'click',
        event_category: 'metagame_bingo',
      });

      bingoStore.setBingoFtueEntryPoint('bingo');
      bingoStore.openBingoSection();
    },
    [bingoStore],
  );

  const disableControls = useMemo(
    () =>
      (uiStore.openFullPageDialog === 'loyalty_program' &&
        loyaltyProgramStore.isLoyaltyProgramDialogOpenWithTutorial) ||
      (uiStore.openFullPageDialog &&
        ['new_tier_congrats', 'tier_monthly_bonus', 'tier_birthday_gift'].includes(uiStore.openFullPageDialog)),
    [loyaltyProgramStore.isLoyaltyProgramDialogOpenWithTutorial, uiStore.openFullPageDialog],
  );

  const hideNavigationBar = useMemo(() => {
    if (!uiStore.openFullPageDialog) {
      return false;
    }

    if (bingoStore.bingoEnabled && ACCOUNT_RELATED_DIALOGS.includes(uiStore.openFullPageDialog)) {
      return true;
    }

    return false;
  }, [uiStore.openFullPageDialog, bingoStore.bingoEnabled]);

  return {
    redeemModeAvailable,
    handleOpenFullPageModal,
    handleOpenStore,
    handleOpenRedeem,
    handleOpenBingo,
    handleRewardOpen,
    disableControls,
    hideNavigationBar,
  };
};
